/**
 * File: assets/basari-lens-addons-form.js
 * Purpose: Lens Add-ons Selection UI — presentation + Controller wiring only.
 * Sprint: 1B.14 Phase B — Lens Add-ons Selection UI
 * Owner: Basari Theme / PDP
 * Dependencies: basari-lens-addons.js (Controller API), drawer host mount
 *
 * Rules:
 *   - Never write directly to lensFlow.lensAddons
 *   - All updates go through LensAddonsController.updateState()
 *   - No pricing calculations / recommendation algorithm / validation / cart
 *   - Expand/collapse, tint, and index selection update UI state only
 */
(function (global) {
  'use strict';

  var BasariLensAddonsForm = global.BasariLensAddonsForm || {};
  var READY_ATTR = 'data-basari-lens-addons-form-ready';
  var ACTIVE_CLASS = 'is-active';
  var CHECKED_CLASS = 'is-checked';
  var EXPANDED_CLASS = 'is-expanded';
  var DISABLED_CLASS = 'is-disabled';

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  function includesValue(list, value) {
    if (!Array.isArray(list) || value == null) return false;
    return list.indexOf(value) !== -1;
  }

  function toggleListValue(list, value, enabled) {
    var next = Array.isArray(list) ? list.slice() : [];
    var index = next.indexOf(value);
    if (enabled && index === -1) {
      next.push(value);
    } else if (!enabled && index !== -1) {
      next.splice(index, 1);
    }
    return next;
  }

  function formatPlaceholderMoney(cents, currency) {
    var amount = Number(cents);
    if (!isFinite(amount)) amount = 0;
    var major = (amount / 100).toFixed(amount % 100 === 0 ? 0 : 2);
    var code = String(currency || '').toUpperCase();
    if (code === 'INR' || code === '') {
      return '₹' + major;
    }
    if (code === 'AED') {
      return 'AED ' + major;
    }
    return code + ' ' + major;
  }

  /**
   * @param {HTMLElement} root - [data-basari-lens-addons-root]
   * @param {Object} options
   * @param {Function} options.getController - returns LensAddonsController
   * @param {Function} [options.onClose]
   * @param {Function} [options.onConfirm]
   * @param {HTMLElement} [options.eventTarget]
   * @param {HTMLElement} [options.totalBar]
   */
  function LensAddonsUI(root, options) {
    this.root = root;
    this.options = options || {};
    this.form = null;
    this.cache = {};
    this.bound = false;
    this._syncing = false;
    this._onHostEvent = this.onHostEvent.bind(this);
    this._onFormEvent = this.onFormEvent.bind(this);
  }

  LensAddonsUI.prototype.getController = function () {
    if (typeof this.options.getController === 'function') {
      return this.options.getController();
    }
    return null;
  };

  LensAddonsUI.prototype.cacheSelectors = function () {
    var form = this.root.querySelector('[data-basari-lens-addons-form]');
    this.form = form;
    this.cache.form = form;
    this.cache.list = form ? form.querySelector('[data-basari-lens-addons-list]') : null;
    this.cache.items = form
      ? Array.prototype.slice.call(form.querySelectorAll('[data-basari-lens-addon-item]'))
      : [];
    this.cache.confirmBtn = form ? form.querySelector('[data-basari-lens-addons-confirm]') : null;
    this.cache.closeBtn = form ? form.querySelector('[data-basari-lens-addons-close]') : null;
    this.cache.a11y = this.root.querySelector('[data-basari-lens-addons-a11y]');
    this.cache.totalBar = this.options.totalBar || null;
  };

  LensAddonsUI.prototype.mount = function () {
    if (!this.root || this.root.getAttribute(READY_ATTR) === 'true') {
      this.refresh();
      return this;
    }

    this.cacheSelectors();
    if (!this.form) return this;

    this.bindEvents();
    this.hydrateFromController();
    this.root.setAttribute(READY_ATTR, 'true');
    return this;
  };

  LensAddonsUI.prototype.refresh = function () {
    this.hydrateFromController();
    return this;
  };

  LensAddonsUI.prototype.bindEvents = function () {
    if (this.bound || !this.form) return;

    this.form.addEventListener('click', this._onFormEvent);
    this.form.addEventListener('keydown', this._onFormEvent);

    var eventTarget = this.options.eventTarget || this.root;
    if (eventTarget && eventTarget.addEventListener) {
      eventTarget.addEventListener('basari:lens-addons:updated', this._onHostEvent);
      eventTarget.addEventListener('basari:lens-addons:reset', this._onHostEvent);
      eventTarget.addEventListener('basari:lens-addons:initialized', this._onHostEvent);
      eventTarget.addEventListener('basari:lens-addons:opened', this._onHostEvent);
    }

    this.bound = true;
  };

  LensAddonsUI.prototype.onHostEvent = function (event) {
    if (!event || this._syncing) return;
    var detail = event.detail || {};
    this.hydrateFromState(detail.state || null);
  };

  LensAddonsUI.prototype.onFormEvent = function (event) {
    if (!event || this._syncing) return;
    var target = event.target;
    if (!target) return;

    if (event.type === 'keydown') {
      this.handleKeydown(event);
      return;
    }

    if (event.type !== 'click') return;

    if (target.closest('[data-basari-lens-addons-confirm]')) {
      event.preventDefault();
      this.handleConfirm();
      return;
    }

    if (target.closest('[data-basari-lens-addons-close]')) {
      event.preventDefault();
      this.handleClose();
      return;
    }

    var indexBtn = target.closest('[data-basari-lens-addon-index]');
    if (indexBtn) {
      event.preventDefault();
      this.handleIndexSelect(indexBtn);
      return;
    }

    var swatchBtn = target.closest('[data-basari-lens-addon-swatch]');
    if (swatchBtn) {
      event.preventDefault();
      this.handleSwatchSelect(swatchBtn);
      return;
    }

    var toggle = target.closest('[data-basari-lens-addon-toggle]');
    if (toggle) {
      event.preventDefault();
      this.handleAddonToggle(toggle);
    }
  };

  LensAddonsUI.prototype.handleKeydown = function (event) {
    var key = event.key || event.code;
    var target = event.target;
    if (!target) return;

    var isSpace = key === ' ' || key === 'Spacebar' || key === 'Space';
    var isEnter = key === 'Enter';
    if (!isSpace && !isEnter) return;

    if (
      target.matches(
        '[data-basari-lens-addon-toggle], [data-basari-lens-addon-index], [data-basari-lens-addon-swatch]'
      )
    ) {
      event.preventDefault();
      target.click();
    }
  };

  LensAddonsUI.prototype.patchController = function (patch) {
    var controller = this.getController();
    if (!controller || typeof controller.updateState !== 'function') return null;
    this._syncing = true;
    try {
      return controller.updateState(patch);
    } finally {
      this._syncing = false;
    }
  };

  LensAddonsUI.prototype.getState = function () {
    var controller = this.getController();
    if (controller && typeof controller.getState === 'function') {
      return controller.getState();
    }
    return null;
  };

  LensAddonsUI.prototype.getConfiguration = function () {
    var controller = this.getController();
    if (controller && typeof controller.getConfiguration === 'function') {
      return controller.getConfiguration();
    }
    return null;
  };

  LensAddonsUI.prototype.handleAddonToggle = function (toggle) {
    if (!toggle || toggle.disabled || toggle.classList.contains(DISABLED_CLASS)) return;

    var addonId = toggle.getAttribute('data-addon-id');
    if (!addonId) return;

    var state = this.getState() || {};
    var selected = Array.isArray(state.selectedOptions) ? state.selectedOptions.slice() : [];
    var expanded = Array.isArray(state.expandedCards) ? state.expandedCards.slice() : [];
    var isChecked = includesValue(selected, addonId);
    var nextChecked = !isChecked;

    selected = toggleListValue(selected, addonId, nextChecked);

    var item = toggle.closest('[data-basari-lens-addon-item]');
    var expandable = item && item.getAttribute('data-expandable') === 'true';

    if (expandable) {
      expanded = toggleListValue(expanded, addonId, nextChecked);
    }

    var patch = {
      selectedOptions: selected,
      expandedCards: expanded
    };

    if (!nextChecked && addonId === 'tinted') {
      patch.selectedTintColor = null;
    }
    if (!nextChecked && addonId === 'thin-lens') {
      patch.selectedLensIndex = null;
    }

    var nextState = this.patchController(patch);
    this.hydrateFromState(nextState || this.getState());
    this.announceSelection(addonId, nextChecked);
  };

  LensAddonsUI.prototype.handleIndexSelect = function (button) {
    if (!button || button.disabled) return;
    var indexId = button.getAttribute('data-index-id');
    if (!indexId) return;

    var state = this.getState() || {};
    var selected = Array.isArray(state.selectedOptions) ? state.selectedOptions.slice() : [];
    var expanded = Array.isArray(state.expandedCards) ? state.expandedCards.slice() : [];

    if (!includesValue(selected, 'thin-lens')) {
      selected = toggleListValue(selected, 'thin-lens', true);
    }
    if (!includesValue(expanded, 'thin-lens')) {
      expanded = toggleListValue(expanded, 'thin-lens', true);
    }

    var nextState = this.patchController({
      selectedOptions: selected,
      expandedCards: expanded,
      selectedLensIndex: indexId
    });
    this.hydrateFromState(nextState || this.getState());
  };

  LensAddonsUI.prototype.handleSwatchSelect = function (button) {
    if (!button || button.disabled) return;
    var swatchId = button.getAttribute('data-swatch-id');
    if (!swatchId) return;

    var state = this.getState() || {};
    var selected = Array.isArray(state.selectedOptions) ? state.selectedOptions.slice() : [];
    var expanded = Array.isArray(state.expandedCards) ? state.expandedCards.slice() : [];

    if (!includesValue(selected, 'tinted')) {
      selected = toggleListValue(selected, 'tinted', true);
    }
    if (!includesValue(expanded, 'tinted')) {
      expanded = toggleListValue(expanded, 'tinted', true);
    }

    var nextState = this.patchController({
      selectedOptions: selected,
      expandedCards: expanded,
      selectedTintColor: swatchId
    });
    this.hydrateFromState(nextState || this.getState());
  };

  LensAddonsUI.prototype.handleConfirm = function () {
    if (typeof this.options.onConfirm === 'function') {
      this.options.onConfirm();
      return;
    }

    var controller = this.getController();
    if (controller && typeof controller.prepareSelectionState === 'function') {
      controller.prepareSelectionState();
    }
    if (controller && typeof controller.close === 'function') {
      controller.close({ closeDrawer: false });
    }
  };

  LensAddonsUI.prototype.handleClose = function () {
    if (typeof this.options.onClose === 'function') {
      this.options.onClose();
      return;
    }

    var controller = this.getController();
    if (controller && typeof controller.close === 'function') {
      controller.close({ closeDrawer: true });
    }
  };

  LensAddonsUI.prototype.hydrateFromController = function () {
    this.hydrateFromState(this.getState());
  };

  LensAddonsUI.prototype.hydrateFromState = function (state) {
    state = state || this.getState() || {};
    var selected = Array.isArray(state.selectedOptions) ? state.selectedOptions : [];
    var expanded = Array.isArray(state.expandedCards) ? state.expandedCards : [];
    var recommended = Array.isArray(state.recommendedOptions)
      ? state.recommendedOptions
      : state.recommendationState && Array.isArray(state.recommendationState.recommendedOptions)
        ? state.recommendationState.recommendedOptions
        : [];
    var recommendedIndex =
      state.recommendedLensIndex != null
        ? String(state.recommendedLensIndex)
        : state.recommendationState && state.recommendationState.recommendedLensIndex != null
          ? String(state.recommendationState.recommendedLensIndex)
          : null;
    var selectedTint = state.selectedTintColor != null ? String(state.selectedTintColor) : null;
    var selectedIndex = state.selectedLensIndex != null ? String(state.selectedLensIndex) : null;

    var self = this;
    this.cache.items.forEach(function (item) {
      self.syncCard(item, {
        selected: selected,
        expanded: expanded,
        recommended: recommended,
        recommendedIndex: recommendedIndex,
        selectedTint: selectedTint,
        selectedIndex: selectedIndex
      });
    });

    this.syncTotalPlaceholder(state);
  };

  LensAddonsUI.prototype.syncCard = function (item, ctx) {
    if (!item || !ctx) return;

    var addonId = item.getAttribute('data-addon-id');
    var card = item.querySelector('[data-basari-lens-addon-card]');
    var toggle = item.querySelector('[data-basari-lens-addon-toggle]');
    var expand = item.querySelector('[data-basari-lens-addon-expand]');
    var expandable = item.getAttribute('data-expandable') === 'true';
    var isChecked = includesValue(ctx.selected, addonId);
    var isExpanded = expandable && includesValue(ctx.expanded, addonId) && isChecked;
    var isRecommended = includesValue(ctx.recommended, addonId);

    if (toggle) {
      toggle.setAttribute('aria-checked', isChecked ? 'true' : 'false');
      if (expandable) {
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      }
      toggle.classList.toggle(CHECKED_CLASS, isChecked);
    }

    if (card) {
      card.classList.toggle(CHECKED_CLASS, isChecked);
      card.classList.toggle(EXPANDED_CLASS, isExpanded);
      card.classList.toggle(ACTIVE_CLASS, isChecked);
    }

    item.classList.toggle(CHECKED_CLASS, isChecked);
    item.classList.toggle(EXPANDED_CLASS, isExpanded);

    if (expand) {
      expand.classList.toggle(EXPANDED_CLASS, isExpanded);
      expand.removeAttribute('hidden');
      expand.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
      var focusables = expand.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      Array.prototype.forEach.call(focusables, function (el) {
        if (isExpanded) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    }

    var cardBadge = null;
    var children = item.children;
    var i;
    for (i = 0; i < children.length; i += 1) {
      if (children[i].hasAttribute && children[i].hasAttribute('data-basari-lens-addon-badge')) {
        cardBadge = children[i];
        break;
      }
    }
    if (cardBadge) {
      setHidden(cardBadge, !isRecommended);
    }

    if (addonId === 'thin-lens') {
      this.syncIndexButtons(item, ctx.selectedIndex, ctx.recommendedIndex);
    }

    if (addonId === 'tinted') {
      this.syncSwatches(item, ctx.selectedTint);
    }
  };

  LensAddonsUI.prototype.syncIndexButtons = function (item, selectedIndex, recommendedIndex) {
    var buttons = item.querySelectorAll('[data-basari-lens-addon-index]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var indexId = btn.getAttribute('data-index-id');
      var isSelected = selectedIndex != null && indexId === selectedIndex;
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      btn.classList.toggle(ACTIVE_CLASS, isSelected);

      var wrap = btn.closest('[data-basari-lens-addon-index-wrap]');
      var badge = wrap ? wrap.querySelector('[data-basari-lens-addon-badge]') : null;
      if (badge) {
        var show = recommendedIndex != null && indexId === recommendedIndex;
        setHidden(badge, !show);
      }
    });
  };

  LensAddonsUI.prototype.syncSwatches = function (item, selectedTint) {
    var swatches = item.querySelectorAll('[data-basari-lens-addon-swatch]');
    Array.prototype.forEach.call(swatches, function (swatch) {
      var swatchId = swatch.getAttribute('data-swatch-id');
      var isSelected = selectedTint != null && swatchId === selectedTint;
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      swatch.classList.toggle(ACTIVE_CLASS, isSelected);
    });
  };

  /**
   * Running total presentation — display controller placeholder only.
   * Does not calculate prices. Leaves existing frame/lens total when pricing disabled.
   * Passive unless the lens-addons route is the active open step.
   */
  LensAddonsUI.prototype.syncTotalPlaceholder = function (state) {
    var totalBar = this.cache.totalBar;
    if (!totalBar) return;

    var stepHost =
      (this.root && this.root.closest && this.root.closest('[data-basari-lens-addons]')) || null;
    if (stepHost && stepHost.hasAttribute('hidden')) return;

    var controller = this.getController();
    if (!controller || controller.isOpen !== true) return;

    var config = this.getConfiguration();
    var labels = config && config.labels ? config.labels : {};
    var toggles = config && config.featureToggles ? config.featureToggles : {};
    var labelEl = totalBar.querySelector('.basari-lens-drawer__total-sum-label');
    var valueEl = totalBar.querySelector('[data-basari-lens-total-value]');

    if (labelEl && labels.totalLabel) {
      labelEl.textContent = labels.totalLabel;
    }

    totalBar.setAttribute(
      'data-addons-placeholder-total',
      state && state.totalPrice != null ? String(state.totalPrice) : '0'
    );

    if (!toggles.enablePricing || !valueEl) return;

    var currency = totalBar.getAttribute('data-currency') || 'INR';
    var cents = state && state.totalPrice != null ? Number(state.totalPrice) || 0 : 0;
    valueEl.textContent = formatPlaceholderMoney(cents, currency);
  };

  LensAddonsUI.prototype.announceSelection = function (addonId, checked) {
    var live = this.cache.a11y;
    if (!live) return;
    var item = this.form
      ? this.form.querySelector('[data-basari-lens-addon-item][data-addon-id="' + addonId + '"]')
      : null;
    var titleEl = item ? item.querySelector('.basari-lens-addons__title') : null;
    var title = titleEl ? titleEl.textContent.trim() : addonId;
    live.textContent = title + (checked ? ' selected' : ' deselected');
  };

  function mount(root, options) {
    var ui = new LensAddonsUI(root, options || {});
    return ui.mount();
  }

  BasariLensAddonsForm.LensAddonsUI = LensAddonsUI;
  BasariLensAddonsForm.mount = mount;

  global.BasariLensAddonsForm = BasariLensAddonsForm;
})(typeof window !== 'undefined' ? window : this);
